import React, { useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material'

import { toast } from 'react-toastify'
import { create, muiDialogV5, useModal } from '@ebay/nice-modal-react'
import { useRequest } from 'ahooks'

import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { commify } from 'ethers/lib/utils'
import { Text3 } from './UnisatTransferBrc20Dialog'
import useDepositAddressByBtc from '@/hooks/useDepositAddressByBtc'
import Dialog from '@/components/common/Dialog'
import useInscirptionOfUser from '@/hooks/useInscirptionOfUser'
import useTokenDepositConfig from '@/hooks/useTokenDepositConfig'

const Title = styled(Box)`
  width: 100%;
  background: var(--ps-primary_bg);
  border: 1px solid rgba(191, 255, 11, 0.2);
  padding: 18px 20px;
  word-break: break-word;
`

interface OkxTransferBrc20DialogProps {
  tokenName: string
}

const transferNft = (receiver: string, inscriptionId: string): Promise<string> => {
  const okxwallet = (window as any).okxwallet.bitcoin
  const account = okxwallet.selectedAccount.address
  if (!receiver || !inscriptionId) return Promise.reject('missing params')
  return okxwallet.transferNft({
    from: account,
    to: receiver,
    data: inscriptionId,
  })
}

const inscribe = (tick: string): Promise<string> => {
  const okxwallet = (window as any).okxwallet.bitcoin
  const account = okxwallet.selectedAccount.address

  return okxwallet.inscribe({
    from: account,
    tick,
  })
}

const OkxTransferBrc20Dialog = ({ tokenName }: OkxTransferBrc20DialogProps) => {
  const modal = useModal()

  const { data: depositAddress } = useDepositAddressByBtc()

  const { data: inscirptionOfUser } = useInscirptionOfUser(tokenName)

  const [inscriptionId, setInscriptionId] = useState<string>('')
  const [inscriptionAmount, setInscriptionAmount] = useState('')

  const { run: _inscribe, loading: isInscribing } = useRequest(inscribe, {
    manual: true,
    onSuccess: (data) => {
      console.log('>> txid: ', data)
      toast.success('The application has been submitted, the transaction is pending confirmation')
      modal.hide()
    },
    onError: (error) => {
      console.log('>> error: ', error)
    },
  })

  const { run: _transfer, loading } = useRequest(transferNft, {
    manual: true,
    onSuccess: (data) => {
      console.log('>> transferNft: ', data)
      toast.success('The application has been submitted, the transaction is pending confirmation.')
      modal.hide()
    },
    onError: (error) => {
      console.log('>> transferNft: ', error)
    },
  })

  const { data: tokenDepositConfig } = useTokenDepositConfig()
  const depositFeeRate = tokenDepositConfig ? tokenDepositConfig?.[tokenName].fee / 10000 : null
  const depositFeeRateInPercent = tokenDepositConfig ? (tokenDepositConfig?.[tokenName].fee * 100) / 10000 : null

  return (
    <Dialog {...muiDialogV5(modal)}>
      <Stack sx={{ textAlign: 'left' }}>
        <Typography fontSize={32} textAlign="center" sx={{ pb: 32 }}>
          Transfer
        </Typography>
        <Stack spacing={8}>
          <Typography fontSize={16}>
            {/* <strong style={{ color: 'var(--ps-primary)' }}>{tokenName}</strong> Deposit  */}
            Address
          </Typography>
          <Title>{depositAddress}</Title>
        </Stack>

        {inscirptionOfUser?.length ? (
          <Stack sx={{ pt: 32 }}>
            <Typography fontSize={16}>Choose an inscription ({inscirptionOfUser.length})</Typography>
            <List sx={{ mt: 8, maxHeight: 200, overflow: 'auto', border: '1px solid rgba(191, 255, 11, 0.2)' }}>
              {inscirptionOfUser.map((v) => (
                <ListItem key={v.inscriptionNumber} sx={{ p: 0 }}>
                  <ListItemButton
                    onClick={() => {
                      setInscriptionId(v.inscriptionId)
                      setInscriptionAmount(v.amount)
                    }}
                    dense
                  >
                    <ListItemIcon>
                      <Radio size="small" checked={inscriptionId === v.inscriptionId} value={v.inscriptionId} />
                    </ListItemIcon>
                    <ListItemText
                      id={v.inscriptionId}
                      primary={
                        <Typography color="#fff" sx={{ flex: 1 }}>
                          {Number(v.amount).toLocaleString('en')}&nbsp;
                          <strong style={{ color: 'var(--ps-primary)' }}>{tokenName}</strong>
                        </Typography>
                      }
                      secondary={
                        <Typography sx={{ opacity: 0.6 }} fontSize={12}>{`#${v.inscriptionNumber}`}</Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Stack>
        ) : null}

        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 4, mt: 12 }}>
          <ElectricalServicesIcon sx={{ color: '#BFFF0B' }} />
          <Text3>
            Service fees:{' '}
            {inscriptionAmount && typeof depositFeeRate === 'number'
              ? `${commify((Number(inscriptionAmount || 0) * depositFeeRate).toFixed(3))} ${tokenName}`
              : '-'}
          </Text3>
          <Tooltip
            title={`Cross-chain service fee will be charged ${
              typeof depositFeeRateInPercent === 'number' ? commify(depositFeeRateInPercent) : '-'
            }%`}
            arrow
          >
            <HelpOutlineIcon sx={{ width: 16, height: 16, opacity: 0.5 }} />
          </Tooltip>
        </Box>

        <Stack sx={{ pt: 32 }} spacing={16}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            disabled={isInscribing}
            onClick={() => {
              _inscribe(tokenName)
            }}
          >
            {isInscribing && (
              <>
                <CircularProgress size={14} />
                &nbsp;
              </>
            )}
            New Inscription
          </Button>
          <Button
            fullWidth
            size="large"
            variant="contained"
            disabled={loading || !inscriptionId}
            onClick={() => {
              _transfer(depositAddress, inscriptionId)
            }}
          >
            {loading && (
              <>
                <CircularProgress size={14} />
                &nbsp;
              </>
            )}
            Confirm
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  )
}

export default create(OkxTransferBrc20Dialog)
